import React, { ReactNode, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import LoginIcon from '@mui/icons-material/Login';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, ROLES } from '../../config/authConfig';

interface AuthGuardProps {
  children: ReactNode;
  requiredRole?: UserRole;
  fallback?: ReactNode;
}

// Demo credential hints — baked in at build time via VITE_ env vars.
// Set VITE_DEMO_LOGIN_HINT and VITE_DEMO_PASSWORD_HINT in Azure SWA config.
const DEMO_EMAIL = import.meta.env.VITE_DEMO_LOGIN_HINT as string | undefined;
const DEMO_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD_HINT as string | undefined;

function hasDemoParam(): boolean {
  return new URLSearchParams(window.location.search).get('demo') === 'true';
}

/** Copy text to clipboard, briefly flash confirmation */
function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  });
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRole = ROLES.ANALYST,
}) => {
  const { isLoading, error, hasRole, login } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, accounts } = useMsal();

  // Show demo landing only when:
  // 1. URL has ?demo=true
  // 2. No existing MSAL session (accounts cache is empty)
  // 3. MSAL is completely idle (not handling a redirect / token acquisition)
  // 4. Not yet authenticated
  // This prevents the card from flashing on page reloads where MSAL
  // restores the session from cache (accounts > 0 before isAuthenticated flips true).
  const showDemoLanding =
    hasDemoParam() &&
    !isAuthenticated &&
    accounts.length === 0 &&
    inProgress === InteractionStatus.None;

  // Auto-redirect when NOT in demo landing mode
  useEffect(() => {
    if (showDemoLanding) return;
    if (!isLoading && !isAuthenticated && !error && inProgress === InteractionStatus.None) {
      login();
    }
  }, [isLoading, isAuthenticated, error, login, inProgress, showDemoLanding]);

  // Loading / redirect in progress — show spinner.
  // If demo param is present AND we have a cached account (returning user on reload),
  // skip the demo card and just show the spinner until MSAL restores the session.
  //
  // NOTE: We intentionally do NOT spin on `userRoles.length === 0`. While claims are
  // still being resolved, `isLoading` is true (set in AuthContext), so the spinner
  // already covers that window. Once resolution finishes, an authenticated user with
  // no role must fall through to the "Access Denied" screen below — spinning on empty
  // roles here would hang forever for any account that lacks an app-role assignment.
  const isSpinning =
    isLoading ||
    inProgress !== InteractionStatus.None ||
    (!isAuthenticated && !showDemoLanding);

  if (isSpinning) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Demo landing — floating credential card, rendered while spinner would show
  if (showDemoLanding) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'grey.100',
        }}
      >
        {/* Spinner underneath */}
        <CircularProgress
          size={40}
          sx={{ position: 'absolute', opacity: 0.25 }}
        />

        {/* Floating credential card */}
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            maxWidth: 420,
            width: '100%',
            mx: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.12em',
                fontSize: '0.7rem',
              }}
            >
              GovIQ — IDP
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
              Demo Environment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Copy the password below, then click <strong>Sign In</strong>.
              <br />
              Your email will be pre-filled automatically.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Credential rows */}
          {(DEMO_EMAIL || DEMO_PASSWORD) ? (
            <Box sx={{ mb: 3 }}>
              {DEMO_EMAIL && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                  >
                    Email
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      px: 1.5,
                      py: 1,
                      backgroundColor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }}
                  >
                    {DEMO_EMAIL}
                  </Box>
                </Box>
              )}
              {DEMO_PASSWORD && (
                <Box sx={{ mt: 2 }}>
                  <CredentialRow label="Password" value={DEMO_PASSWORD} masked />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Use your demo credentials to sign in.
              </Typography>
            </Box>
          )}

          {/* Sign In */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<LoginIcon />}
            onClick={() => login(DEMO_EMAIL)}
            sx={{ fontWeight: 600 }}
          >
            Sign In to Demo
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 2 }}
          >
            Credentials provided for demonstration purposes only.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Role-based access check
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2">
            You don't have the required permissions to access this application.
            Please contact your administrator if you believe this is an error.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Required role: {requiredRole}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

// ---------------------------------------------------------------------------
// CredentialRow — label + monospace value box with a copy button
// ---------------------------------------------------------------------------

interface CredentialRowProps {
  label: string;
  value: string;
  masked?: boolean;
}

const CredentialRow: React.FC<CredentialRowProps> = ({ label, value, masked = false }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          mt: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            userSelect: 'all',
            wordBreak: 'break-all',
          }}
        >
          {masked ? '•'.repeat(value.length) : value}
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={handleCopy}
          startIcon={<ContentCopyIcon sx={{ fontSize: '0.875rem !important' }} />}
          sx={{
            minWidth: 0,
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            color: copied ? 'success.main' : 'text.secondary',
            flexShrink: 0,
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
    </Box>
  );
};
